<template>
  <div class="ag">
    <!-- HERO -->
    <header class="ag-hero">
      <div class="ag-hero__inner">
        <div class="ag-hero__main">
          <div class="ag-eyebrow">
            <i class="bi bi-pie-chart-fill"></i> Informes · Por categoría
          </div>
          <h1 class="ag-hero__titulo">{{ tituloRango }}</h1>
          <div class="ag-hero__cifra">{{ euros(totalPeriodo) }}</div>
          <p class="ag-hero__frase">
            <template v-if="ranking.length">
              <strong>{{ ranking[0].categoria }}</strong> se lleva el
              <strong>{{ ranking[0].pct.toFixed(0) }}%</strong> de lo que has gastado
              <template v-if="ranking.length > 1"> y {{ ranking.length - 1 }} categorías más se reparten el resto.</template>
            </template>
            <template v-else>No hay gastos en este periodo.</template>
          </p>
          <div class="d-flex flex-wrap gap-2 align-items-center mb-3">
            <span v-if="deltaPeriodo != null" class="ag-chip-delta" :class="deltaPeriodo > 0 ? 'sube' : 'baja'">
              <i class="bi" :class="deltaPeriodo > 0 ? 'bi-arrow-up-right' : 'bi-arrow-down-right'"></i>
              {{ textoVariacion(deltaPeriodo) }} vs. periodo anterior
            </span>
            <span v-if="periodoPrevio" class="ag-chip-delta">
              Antes: {{ euros(totalPrevio, { redondo: true }) }}
            </span>
          </div>
          <div class="ag-pills" role="group" aria-label="Rango de fechas">
            <button
              v-for="p in presets"
              :key="p.id"
              type="button"
              class="ag-pill"
              :class="{ activo: presetActivo === p.id }"
              :title="p.titulo"
              @click="aplicarRango(p.rango())"
            >{{ p.label }}</button>
            <button type="button" class="ag-pill" :class="{ activo: presetActivo === 'ninguno' }" @click="quitarFiltroFecha">
              Todo
            </button>
          </div>
          <div class="ag-fechas">
            <label class="visually-hidden" for="informe-fecha-inicio">Fecha inicio</label>
            <input id="informe-fecha-inicio" v-model="fechaDesde" type="date" :max="fechaHasta || undefined" title="Fecha inicio (inclusive)" />
            <span class="align-self-center small" style="color:#a9c7b6">→</span>
            <label class="visually-hidden" for="informe-fecha-fin">Fecha fin</label>
            <input id="informe-fecha-fin" v-model="fechaHasta" type="date" :min="fechaDesde || undefined" title="Fecha fin (inclusive)" />
            <button type="button" class="ag-btn ag-btn--hero ms-auto" @click="abrirGestionCategorias">
              <i class="bi bi-tags me-1"></i>Categorías
            </button>
          </div>
        </div>
        <div class="ag-hero__side">
          <div class="ag-hero__stats">
            <div class="ag-stat">
              <div class="ag-stat__label">Gastos</div>
              <div class="ag-stat__valor">{{ gastosPeriodo.length }}</div>
              <div class="ag-stat__sub">{{ ranking.length }} categorías</div>
            </div>
            <div class="ag-stat">
              <div class="ag-stat__label">Ticket medio</div>
              <div class="ag-stat__valor">{{ euros(ticketMedio) }}</div>
              <div class="ag-stat__sub">por gasto</div>
            </div>
            <div class="ag-stat">
              <div class="ag-stat__label">Al día</div>
              <div class="ag-stat__valor">{{ euros(mediaDiaria) }}</div>
              <div class="ag-stat__sub">{{ diasPeriodo }} días</div>
            </div>
            <div class="ag-stat">
              <div class="ag-stat__label">Mayor gasto</div>
              <div class="ag-stat__valor">{{ mayorGasto ? euros(importe(mayorGasto)) : '—' }}</div>
              <div class="ag-stat__sub">{{ mayorGasto?.concepto || '' }}</div>
            </div>
          </div>
        </div>
      </div>
    </header>

    <div class="ag-body">
      <!-- INSIGHTS -->
      <div v-if="insights.length" class="ag-insights">
        <div v-for="(ins, i) in insights" :key="i" class="ag-insight">
          <div class="ag-insight__icono"><i class="bi" :class="ins.icono"></i></div>
          <div class="ag-insight__texto" v-html="ins.html"></div>
        </div>
      </div>

      <div class="ag-grid">
        <!-- DONUT -->
        <section class="ag-card ag-span-5">
          <div class="ag-card__head">
            <h2 class="ag-card__titulo">Reparto</h2>
            <span class="ag-card__sub">Toca un sector para filtrar</span>
          </div>
          <div v-if="!ranking.length" class="ag-vacio">No hay datos para el periodo seleccionado</div>
          <template v-else>
            <div class="ag-donut">
              <Doughnut :data="donutData" :options="donutOptions" />
              <div class="ag-donut__centro">
                <div class="ag-donut__label">{{ centro.label }}</div>
                <div class="ag-donut__cifra">{{ euros(centro.total, { redondo: centro.total >= 1000 }) }}</div>
                <div class="ag-donut__pct">{{ centro.pct }}</div>
              </div>
            </div>
            <div class="ag-tira mt-3" aria-hidden="true">
              <span
                v-for="seg in segmentosDonut"
                :key="seg.categoria"
                :style="{ flexGrow: seg.total, background: seg.color, opacity: categoriaSeleccionada && categoriaSeleccionada !== seg.categoria ? 0.3 : 1 }"
                :title="`${seg.categoria}: ${euros(seg.total)}`"
                @click="seleccionarCategoria(seg.categoria)"
              ></span>
            </div>
          </template>
        </section>

        <!-- RANKING -->
        <section class="ag-card ag-span-7">
          <div class="ag-card__head">
            <h2 class="ag-card__titulo">Ranking de categorías</h2>
            <span class="ag-card__sub">
              <template v-if="periodoPrevio">Variación vs. periodo anterior · </template>últimos 6 meses
            </span>
          </div>
          <div v-if="!ranking.length" class="ag-vacio">Sin gastos en este periodo</div>
          <ul v-else class="ag-rank">
            <li
              v-for="cat in rankingVisible"
              :key="cat.categoria"
              class="ag-rank__item ag-rank__item--spark"
              :class="{ activo: categoriaSeleccionada === cat.categoria, atenuado: categoriaSeleccionada && categoriaSeleccionada !== cat.categoria }"
              role="button"
              tabindex="0"
              @click="seleccionarCategoria(cat.categoria)"
              @keydown.enter="seleccionarCategoria(cat.categoria)"
            >
              <span class="ag-dot" :style="{ background: colorCat(cat.categoria) }"></span>
              <span class="ag-rank__nombre">{{ cat.categoria }}</span>
              <span class="d-flex align-items-center gap-2">
                <AgSparkline class="d-none d-sm-block" :valores="sparkCat(cat.categoria)" :color="colorCat(cat.categoria)" />
                <span class="ag-rank__valor">{{ euros(cat.total) }}</span>
              </span>
              <span class="ag-rank__barra">
                <span :style="{ width: `${(cat.total / ranking[0].total) * 100}%`, background: colorCat(cat.categoria) }"></span>
              </span>
              <span class="ag-rank__meta">
                <span>{{ cat.pct.toFixed(1) }}% · {{ cat.num }} {{ cat.num === 1 ? 'gasto' : 'gastos' }}</span>
                <span v-if="periodoPrevio" class="ag-delta" :class="claseDelta(deltaCat(cat))">
                  <template v-if="previoPorCat[cat.categoria]">{{ textoVariacion(deltaCat(cat)) }}</template>
                  <template v-else>nueva</template>
                </span>
              </span>
            </li>
          </ul>
          <div v-if="ranking.length > 8" class="text-center mt-2">
            <button type="button" class="ag-btn" @click="verTodas = !verTodas">
              {{ verTodas ? 'Ver menos' : `Ver las ${ranking.length} categorías` }}
            </button>
          </div>
        </section>
      </div>

      <!-- FICHA DE CATEGORÍA -->
      <section v-if="ficha" class="ag-card mb-3 ag-ficha" :style="{ '--cat': colorCat(ficha.categoria) }">
        <div class="ag-card__head">
          <div class="d-flex align-items-center gap-2">
            <span class="ag-dot" :style="{ background: colorCat(ficha.categoria), width: '1rem', height: '1rem' }"></span>
            <h2 class="ag-card__titulo">{{ ficha.categoria }}</h2>
          </div>
          <button type="button" class="ag-btn" @click="categoriaSeleccionada = null">
            <i class="bi bi-x-lg me-1"></i>Cerrar ficha
          </button>
        </div>
        <div class="ag-kpis mb-3">
          <div class="ag-kpi">
            <div class="ag-kpi__label">Total</div>
            <div class="ag-kpi__valor">{{ euros(ficha.total) }}</div>
            <div class="ag-kpi__sub">{{ ficha.pct.toFixed(1) }}% del periodo</div>
          </div>
          <div class="ag-kpi">
            <div class="ag-kpi__label">Gastos</div>
            <div class="ag-kpi__valor">{{ ficha.num }}</div>
            <div class="ag-kpi__sub">ticket medio {{ euros(ficha.total / ficha.num) }}</div>
          </div>
          <div class="ag-kpi">
            <div class="ag-kpi__label">Mayor gasto</div>
            <div class="ag-kpi__valor">{{ euros(importe(ficha.mayor)) }}</div>
            <div class="ag-kpi__sub">{{ ficha.mayor.concepto }}</div>
          </div>
          <div class="ag-kpi">
            <div class="ag-kpi__label">Frecuencia</div>
            <div class="ag-kpi__valor">{{ ficha.frecuencia }}</div>
            <div class="ag-kpi__sub">entre gastos</div>
          </div>
        </div>
        <div class="row g-4">
          <div class="col-md-6">
            <h3 class="ag-subtitulo">Conceptos que más pesan</h3>
            <ul class="ag-rank">
              <li v-for="c in ficha.conceptos" :key="c.concepto" class="ag-rank__item" style="cursor: default">
                <span class="ag-dot" :style="{ background: colorCat(ficha.categoria) }"></span>
                <span class="ag-rank__nombre">{{ c.concepto }}</span>
                <span class="ag-rank__valor">{{ euros(c.total) }}</span>
                <span class="ag-rank__barra">
                  <span :style="{ width: `${(c.total / ficha.conceptos[0].total) * 100}%`, background: colorCat(ficha.categoria) }"></span>
                </span>
                <span class="ag-rank__meta"><span>{{ c.num }}×</span></span>
              </li>
            </ul>
          </div>
          <div class="col-md-6">
            <h3 class="ag-subtitulo">¿Qué día de la semana?</h3>
            <div class="ag-semana">
              <div v-for="(v, i) in ficha.semana" :key="i" class="ag-semana__col" :title="`${DIAS_SEMANA[i]}: ${euros(v)}`">
                <div class="ag-semana__valor">{{ v > 0 ? euros(v, { redondo: true }) : '' }}</div>
                <div class="ag-semana__barra">
                  <span :style="{ height: `${ficha.maxSemana ? (v / ficha.maxSemana) * 100 : 0}%`, background: colorCat(ficha.categoria) }"></span>
                </div>
                <div class="ag-semana__dia">{{ DIAS_SEMANA[i].slice(0, 3) }}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- EVOLUCIÓN APILADA -->
      <section class="ag-card mb-3">
        <div class="ag-card__head">
          <div>
            <h2 class="ag-card__titulo">Evolución por categoría</h2>
            <div class="ag-card__sub">Últimos 12 meses hasta {{ nombreMes(mesFinalSerie) }}</div>
          </div>
          <div class="ag-leyenda">
            <span v-for="c in categoriasSerie" :key="c"><i :style="{ background: colorCat(c), height: '0.7rem', width: '0.7rem', borderRadius: '3px' }"></i>{{ c }}</span>
          </div>
        </div>
        <div class="ag-chart">
          <Bar :data="evolucionData" :options="evolucionOptions" />
        </div>
      </section>

      <!-- INGRESOS POR CATEGORÍA -->
      <section v-if="hasIngresos && ingresosCategoria.length" class="ag-card mb-3">
        <div class="ag-card__head">
          <div>
            <h2 class="ag-card__titulo"><i class="bi bi-wallet2 me-1"></i>Ingresos por categoría</h2>
            <div class="ag-card__sub">Todos los periodos (no usa el filtro de fecha)</div>
          </div>
          <span class="ag-cifra fs-5" style="color: var(--ingreso)">{{ euros(totalIngresosCategoria) }}</span>
        </div>
        <ul class="ag-rank">
          <li v-for="ing in ingresosCategoria" :key="ing.categoria" class="ag-rank__item" style="cursor: default">
            <span class="ag-dot" style="background: var(--ingreso)"></span>
            <span class="ag-rank__nombre">{{ ing.categoria }}</span>
            <span class="ag-rank__valor">{{ euros(ing.total) }}</span>
            <span class="ag-rank__barra">
              <span :style="{ width: `${totalIngresosCategoria ? (ing.total / ingresosCategoria[0].total) * 100 : 0}%`, background: 'var(--ingreso)' }"></span>
            </span>
            <span class="ag-rank__meta">
              <span>{{ totalIngresosCategoria ? ((ing.total / totalIngresosCategoria) * 100).toFixed(1) : 0 }}%</span>
            </span>
          </li>
        </ul>
      </section>

      <!-- LISTA -->
      <AgListaGastos
        :gastos="gastosLista"
        :colores="colores"
        :titulo="categoriaSeleccionada ? `Gastos · ${categoriaSeleccionada}` : 'Gastos del periodo'"
      >
        <template #filtros>
          <span v-if="categoriaSeleccionada" class="ag-filtro-activo">
            {{ categoriaSeleccionada }}
            <button type="button" aria-label="Quitar filtro" @click="categoriaSeleccionada = null"><i class="bi bi-x"></i></button>
          </span>
        </template>
      </AgListaGastos>
    </div>

    <!-- Offcanvas: Gestión de Categorías -->
    <div id="offcanvasCategorias" ref="offcanvasRef" class="offcanvas offcanvas-end" tabindex="-1" style="width: 400px; max-width: 100vw;">
      <div class="offcanvas-header bg-light border-bottom">
        <h5 class="offcanvas-title"><i class="bi bi-tags me-2"></i>Gestión de Categorías</h5>
        <button type="button" class="btn-close" @click="cerrarGestionCategorias"></button>
      </div>
      <div class="offcanvas-body">
        <categoria-form
          :edit-mode="!!categoriaToEdit"
          :categoria-to-edit="categoriaToEdit"
          @categoria-added="onCategoriaGuardada"
          @categoria-updated="onCategoriaGuardada"
          @cancel-edit="categoriaToEdit = null"
        />
        <div class="list-group mt-3">
          <div
            v-for="cat in listaCategorias"
            :key="cat.id"
            class="list-group-item d-flex justify-content-between align-items-center"
          >
            <span><span class="ag-dot me-2" :style="{ background: colorCat(cat.nombre) }"></span>{{ cat.nombre }}</span>
            <div class="btn-group btn-group-sm">
              <button class="btn btn-outline-primary" @click="categoriaToEdit = { ...cat }">
                <i class="bi bi-pencil"></i>
              </button>
              <button class="btn btn-outline-danger" @click="eliminarCategoria(cat.id)">
                <i class="bi bi-trash"></i>
              </button>
            </div>
          </div>
          <div v-if="listaCategorias.length === 0" class="list-group-item text-muted text-center">
            No hay categorías
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue';
import axios from '../config/axios';
import { Doughnut, Bar } from 'vue-chartjs';
import { Chart as ChartJS, ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';
import { useStore } from 'vuex';
import * as bootstrap from 'bootstrap';
import {
  rangoUltimoMesNatural,
  rangoMesEnCurso,
  rangoAnoEnCurso,
  rangoUltimoAnoNatural,
  gastoEnRangoFecha
} from '../utils/filtrosFechaInformes';
import {
  importe, diaISO, categoriaDe, euros, sumar, variacion, textoVariacion,
  mapaColoresCategorias, rankingCategorias, periodoAnterior, diasEntre,
  serieMensual, perfilSemanal, topConceptos, nombreMes, aISO, parseISO,
  COLOR_OTRAS, OTRAS, DIAS_SEMANA, MESES_CORTOS, PALETA_CATEGORIAS
} from '../utils/analisisGastos';
import CategoriaForm from '../components/CategoriaForm.vue';
import AgListaGastos from '../components/analisis/AgListaGastos.vue';
import AgSparkline from '../components/analisis/AgSparkline.vue';
import '../assets/analisis-gastos.css';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const escapar = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

export default {
  name: 'GastosPorCategoriaView',
  components: { Doughnut, Bar, CategoriaForm, AgListaGastos, AgSparkline },

  setup() {
    const store = useStore();
    const hasIngresos = computed(() => store.getters['auth/hasIngresos']);
    const ingresosCategoria = ref([]);
    const totalIngresosCategoria = computed(() => ingresosCategoria.value.reduce((s, i) => s + i.total, 0));

    // ---------- Gestión de categorías ----------
    const offcanvasRef = ref(null);
    const offcanvasInstance = ref(null);
    const listaCategorias = ref([]);
    const categoriaToEdit = ref(null);

    const abrirGestionCategorias = () => {
      if (!offcanvasInstance.value && offcanvasRef.value) {
        offcanvasInstance.value = new bootstrap.Offcanvas(offcanvasRef.value);
      }
      offcanvasInstance.value?.show();
    };
    const cerrarGestionCategorias = () => offcanvasInstance.value?.hide();

    const cargarListaCategorias = async () => {
      try {
        const { data } = await axios.get('/api/categorias');
        listaCategorias.value = data;
      } catch (err) {
        console.error('Error al cargar categorías:', err);
      }
    };

    const cargarTodosLosGastos = async () => {
      try {
        const { data } = await axios.get('/api/gastos/todos');
        store.commit('setGastos', data);
      } catch (err) {
        console.error('Error al cargar todos los gastos:', err);
      }
    };

    const onCategoriaGuardada = async () => {
      categoriaToEdit.value = null;
      await Promise.all([cargarListaCategorias(), cargarTodosLosGastos()]);
    };

    const eliminarCategoria = async (id) => {
      if (!confirm('¿Eliminar esta categoría?')) return;
      try {
        await axios.delete(`/api/categorias/${id}`);
        await Promise.all([cargarListaCategorias(), cargarTodosLosGastos()]);
      } catch (err) {
        console.error('Error:', err);
        alert('Error al eliminar la categoría');
      }
    };

    // ---------- Rango de fechas ----------
    const rangoInicial = rangoUltimoMesNatural();
    const fechaDesde = ref(rangoInicial.desde);
    const fechaHasta = ref(rangoInicial.hasta);

    const presets = [
      { id: 'ultimo-mes', label: 'Último mes', titulo: 'Desde el mismo día hace un mes hasta hoy', rango: rangoUltimoMesNatural },
      { id: 'mes-curso', label: 'Este mes', titulo: 'Mes natural en curso', rango: rangoMesEnCurso },
      { id: 'ano-curso', label: 'Este año', titulo: 'Año natural en curso', rango: rangoAnoEnCurso },
      { id: 'ultimo-ano', label: 'Últimos 12 meses', titulo: 'Desde el mismo día hace un año hasta hoy', rango: rangoUltimoAnoNatural }
    ];

    const presetActivo = computed(() => {
      if (!fechaDesde.value && !fechaHasta.value) return 'ninguno';
      const p = presets.find((x) => {
        const r = x.rango();
        return r.desde === fechaDesde.value && r.hasta === fechaHasta.value;
      });
      return p ? p.id : 'personalizado';
    });

    const aplicarRango = (r) => { fechaDesde.value = r.desde; fechaHasta.value = r.hasta; };
    const quitarFiltroFecha = () => { fechaDesde.value = ''; fechaHasta.value = ''; };

    const fechaCorta = (iso) => parseISO(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
    const tituloRango = computed(() => {
      if (fechaDesde.value && fechaHasta.value) return `${fechaCorta(fechaDesde.value)} – ${fechaCorta(fechaHasta.value)}`;
      if (fechaDesde.value) return `Desde el ${fechaCorta(fechaDesde.value)}`;
      if (fechaHasta.value) return `Hasta el ${fechaCorta(fechaHasta.value)}`;
      return 'Todo el historial';
    });

    // ---------- Datos ----------
    const todos = computed(() => store.getters.getGastos || []);
    const colores = computed(() => mapaColoresCategorias(todos.value));
    const colorCat = (c) => colores.value[c] || COLOR_OTRAS;

    const gastosPeriodo = computed(() => todos.value.filter((g) => gastoEnRangoFecha(g, fechaDesde.value, fechaHasta.value)));
    const totalPeriodo = computed(() => sumar(gastosPeriodo.value));
    const ranking = computed(() => rankingCategorias(gastosPeriodo.value));

    const periodoPrevio = computed(() => periodoAnterior(fechaDesde.value, fechaHasta.value));
    const gastosPrevios = computed(() => {
      const p = periodoPrevio.value;
      return p ? todos.value.filter((g) => gastoEnRangoFecha(g, p.desde, p.hasta)) : [];
    });
    const totalPrevio = computed(() => sumar(gastosPrevios.value));
    const deltaPeriodo = computed(() => (periodoPrevio.value ? variacion(totalPeriodo.value, totalPrevio.value) : null));
    const previoPorCat = computed(() => {
      const m = {};
      rankingCategorias(gastosPrevios.value).forEach((c) => { m[c.categoria] = c.total; });
      return m;
    });
    const deltaCat = (cat) => variacion(cat.total, previoPorCat.value[cat.categoria]);
    const claseDelta = (v) => (v == null ? 'neutro' : v > 0.5 ? 'sube' : v < -0.5 ? 'baja' : 'neutro');

    const diasPeriodo = computed(() => {
      const fechas = gastosPeriodo.value.map(diaISO).filter(Boolean).sort();
      const hoy = aISO(new Date());
      const desde = fechaDesde.value || fechas[0];
      let hasta = fechaHasta.value || fechas[fechas.length - 1];
      if (!desde || !hasta) return 0;
      if (hasta > hoy && desde <= hoy) hasta = hoy;
      return Math.max(1, diasEntre(desde, hasta));
    });
    const ticketMedio = computed(() => (gastosPeriodo.value.length ? totalPeriodo.value / gastosPeriodo.value.length : 0));
    const mediaDiaria = computed(() => (diasPeriodo.value ? totalPeriodo.value / diasPeriodo.value : 0));
    const mayorGasto = computed(() => gastosPeriodo.value.reduce((m, g) => (!m || importe(g) > importe(m) ? g : m), null));

    // ---------- Selección ----------
    const categoriaSeleccionada = ref(null);
    const verTodas = ref(false);
    const hoverCat = ref(null);
    const seleccionarCategoria = (c) => {
      if (!c || c === OTRAS) return;
      categoriaSeleccionada.value = categoriaSeleccionada.value === c ? null : c;
    };
    watch(ranking, (r) => {
      if (categoriaSeleccionada.value && !r.some((c) => c.categoria === categoriaSeleccionada.value)) {
        categoriaSeleccionada.value = null;
      }
    });

    const rankingVisible = computed(() => (verTodas.value ? ranking.value : ranking.value.slice(0, 8)));

    // ---------- Donut ----------
    const segmentosDonut = computed(() => {
      const principales = [];
      let otras = 0;
      ranking.value.forEach((c) => {
        if (colorCat(c.categoria) === COLOR_OTRAS) otras += c.total;
        else principales.push({ categoria: c.categoria, total: c.total, color: colorCat(c.categoria) });
      });
      if (otras > 0) principales.push({ categoria: OTRAS, total: otras, color: COLOR_OTRAS });
      return principales;
    });

    const donutData = computed(() => ({
      labels: segmentosDonut.value.map((s) => s.categoria),
      datasets: [{
        data: segmentosDonut.value.map((s) => s.total),
        backgroundColor: segmentosDonut.value.map((s) =>
          categoriaSeleccionada.value && categoriaSeleccionada.value !== s.categoria ? `${s.color}40` : s.color
        ),
        borderColor: '#fbfcf9',
        borderWidth: 2,
        hoverOffset: 10,
        borderRadius: 4
      }]
    }));

    const donutOptions = computed(() => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: '68%',
      layout: { padding: 10 },
      animation: { animateRotate: true, duration: 900, easing: 'easeOutQuart' },
      onHover: (_evt, els) => {
        hoverCat.value = els.length ? segmentosDonut.value[els[0].index]?.categoria : null;
      },
      onClick: (_evt, els) => {
        if (els.length) seleccionarCategoria(segmentosDonut.value[els[0].index]?.categoria);
      },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false }
      }
    }));

    const centro = computed(() => {
      const nombre = hoverCat.value || categoriaSeleccionada.value;
      const seg = nombre && segmentosDonut.value.find((s) => s.categoria === nombre);
      if (seg) {
        return {
          label: seg.categoria,
          total: seg.total,
          pct: `${totalPeriodo.value ? ((seg.total / totalPeriodo.value) * 100).toFixed(1) : 0}% del total`
        };
      }
      return { label: 'Total', total: totalPeriodo.value, pct: `${ranking.value.length} categorías` };
    });

    // ---------- Serie mensual (sparklines + apilado) ----------
    const mesFinalSerie = computed(() => {
      const hoy = aISO(new Date()).slice(0, 7);
      const h = (fechaHasta.value || aISO(new Date())).slice(0, 7);
      return h > hoy ? hoy : h;
    });

    const gastosPorCategoria = computed(() => {
      const m = {};
      todos.value.forEach((g) => { (m[categoriaDe(g)] = m[categoriaDe(g)] || []).push(g); });
      return m;
    });
    const sparkCat = (c) => serieMensual(gastosPorCategoria.value[c] || [], mesFinalSerie.value, 6).map((x) => x.total);

    const categoriasSerie = computed(() => {
      const principales = Object.keys(colores.value).filter((c) => colores.value[c] !== COLOR_OTRAS);
      const hayOtras = Object.keys(colores.value).length > principales.length;
      return hayOtras ? [...principales, OTRAS] : principales;
    });

    const evolucionData = computed(() => {
      const meses = serieMensual([], mesFinalSerie.value, 12).map((x) => x.mes);
      const otrasGastos = Object.keys(gastosPorCategoria.value)
        .filter((c) => colorCat(c) === COLOR_OTRAS)
        .flatMap((c) => gastosPorCategoria.value[c]);
      return {
        labels: meses.map((m) => `${MESES_CORTOS[parseInt(m.slice(5), 10) - 1]} '${m.slice(2, 4)}`),
        datasets: categoriasSerie.value.map((c) => {
          const lista = c === OTRAS ? otrasGastos : gastosPorCategoria.value[c] || [];
          const color = c === OTRAS ? COLOR_OTRAS : colorCat(c);
          const atenuar = categoriaSeleccionada.value && categoriaSeleccionada.value !== c;
          return {
            label: c,
            data: serieMensual(lista, mesFinalSerie.value, 12).map((x) => x.total),
            backgroundColor: atenuar ? `${color}33` : color,
            borderColor: '#fbfcf9',
            borderWidth: { top: 2 },
            borderSkipped: 'bottom',
            maxBarThickness: 38
          };
        })
      };
    });

    const evolucionOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 700 },
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          itemSort: (a, b) => b.raw - a.raw,
          filter: (item) => item.raw > 0,
          callbacks: {
            label: (ctx) => ` ${ctx.dataset.label}: ${euros(ctx.raw)}`,
            footer: (items) => `Total: ${euros(items.reduce((s, i) => s + i.raw, 0))}`
          }
        }
      },
      scales: {
        x: { stacked: true, grid: { display: false }, ticks: { color: '#5c675e' } },
        y: {
          stacked: true,
          beginAtZero: true,
          border: { display: false },
          grid: { color: 'rgba(22, 53, 40, 0.06)' },
          ticks: { color: '#5c675e', callback: (v) => euros(v, { redondo: true }) }
        }
      }
    };

    // ---------- Ficha ----------
    const ficha = computed(() => {
      const c = categoriaSeleccionada.value;
      if (!c) return null;
      const lista = gastosPeriodo.value.filter((g) => categoriaDe(g) === c);
      if (!lista.length) return null;
      const r = ranking.value.find((x) => x.categoria === c);
      const semana = perfilSemanal(lista);
      const fechas = [...new Set(lista.map(diaISO))].sort();
      let frecuencia = '—';
      if (fechas.length > 1) {
        const media = (diasEntre(fechas[0], fechas[fechas.length - 1]) - 1) / (fechas.length - 1);
        frecuencia = media < 1.5 ? 'a diario' : `cada ${Math.round(media)} días`;
      }
      return {
        categoria: c,
        total: r.total,
        num: r.num,
        pct: r.pct,
        mayor: lista.reduce((m, g) => (importe(g) > importe(m) ? g : m), lista[0]),
        conceptos: topConceptos(lista, 6),
        semana,
        maxSemana: Math.max(...semana),
        frecuencia
      };
    });

    // ---------- Insights ----------
    const insights = computed(() => {
      const out = [];
      const r = ranking.value;
      if (!r.length) return out;
      out.push({
        icono: 'bi-trophy',
        html: `<strong>${escapar(r[0].categoria)}</strong> lidera con <strong>${euros(r[0].total)}</strong>: ${r[0].pct.toFixed(0)}% del gasto.`
      });
      let acc = 0;
      let k = 0;
      for (const c of r) { acc += c.pct; k += 1; if (acc >= 80) break; }
      if (r.length > 2) {
        out.push({
          icono: 'bi-funnel',
          html: `El <strong>80%</strong> de tu gasto se concentra en <strong>${k} de ${r.length}</strong> categorías.`
        });
      }
      if (periodoPrevio.value && gastosPrevios.value.length) {
        const cambios = r.map((c) => ({ ...c, dif: c.total - (previoPorCat.value[c.categoria] || 0) }));
        Object.keys(previoPorCat.value).forEach((cat) => {
          if (!r.some((c) => c.categoria === cat)) cambios.push({ categoria: cat, total: 0, dif: -previoPorCat.value[cat] });
        });
        const sube = cambios.reduce((m, c) => (c.dif > (m?.dif ?? 0) ? c : m), null);
        const baja = cambios.reduce((m, c) => (c.dif < (m?.dif ?? 0) ? c : m), null);
        if (sube) {
          out.push({
            icono: 'bi-graph-up-arrow',
            html: `<strong>${escapar(sube.categoria)}</strong> es la que más sube: <strong>+${euros(sube.dif, { redondo: true })}</strong> frente al periodo anterior.`
          });
        }
        if (baja) {
          out.push({
            icono: 'bi-graph-down-arrow',
            html: `Donde más ahorras: <strong>${escapar(baja.categoria)}</strong>, <strong>${euros(baja.dif, { redondo: true })}</strong> menos que antes.`
          });
        }
      }
      if (out.length < 4) {
        const semana = perfilSemanal(gastosPeriodo.value);
        const idx = semana.indexOf(Math.max(...semana));
        if (semana[idx] > 0) {
          out.push({
            icono: 'bi-calendar-week',
            html: `Los <strong>${DIAS_SEMANA[idx]}${idx < 5 ? '' : 's'}</strong> son tu día más caro: <strong>${euros(semana[idx], { redondo: true })}</strong> en el periodo.`
          });
        }
      }
      return out.slice(0, 4);
    });

    // ---------- Lista ----------
    const gastosLista = computed(() =>
      categoriaSeleccionada.value
        ? gastosPeriodo.value.filter((g) => categoriaDe(g) === categoriaSeleccionada.value)
        : gastosPeriodo.value
    );

    onMounted(async () => {
      const promises = [cargarTodosLosGastos(), cargarListaCategorias()];
      if (hasIngresos.value) {
        promises.push(
          axios.get('/api/ingresos/stats/por-categoria')
            .then((r) => { ingresosCategoria.value = [...r.data].sort((a, b) => b.total - a.total); })
            .catch(() => { ingresosCategoria.value = []; })
        );
      }
      await Promise.all(promises);
    });

    return {
      // rango
      fechaDesde, fechaHasta, presets, presetActivo, aplicarRango, quitarFiltroFecha, tituloRango,
      // datos
      colores, colorCat, gastosPeriodo, totalPeriodo, ranking, rankingVisible, verTodas,
      periodoPrevio, totalPrevio, deltaPeriodo, previoPorCat, deltaCat, claseDelta,
      diasPeriodo, ticketMedio, mediaDiaria, mayorGasto,
      categoriaSeleccionada, seleccionarCategoria,
      segmentosDonut, donutData, donutOptions, centro,
      mesFinalSerie, sparkCat, categoriasSerie, evolucionData, evolucionOptions,
      ficha, insights, gastosLista,
      // ingresos
      hasIngresos, ingresosCategoria, totalIngresosCategoria,
      // gestión
      offcanvasRef, listaCategorias, categoriaToEdit, abrirGestionCategorias, cerrarGestionCategorias,
      onCategoriaGuardada, eliminarCategoria,
      // helpers
      euros, importe, textoVariacion, nombreMes, DIAS_SEMANA, PALETA_CATEGORIAS
    };
  }
};
</script>

<style scoped>
.ag-donut { position: relative; height: 290px; }
.ag-donut__centro {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  text-align: center;
  padding: 0 28%;
}
.ag-donut__label {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--tinta-suave);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ag-donut__cifra { font-family: var(--fuente-cifra); font-variant-numeric: tabular-nums; font-size: 1.6rem; line-height: 1.15; color: var(--tinta); }
.ag-donut__pct { font-size: 0.78rem; color: var(--tinta-suave); }
.ag-rank__item--spark .ag-rank__valor { min-width: 5.5rem; }
.ag-subtitulo { font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.08em; color: var(--tinta-suave); font-weight: 700; margin-bottom: 0.5rem; }
.ag-ficha { border-top: 4px solid var(--cat); animation: ag-entrar 0.35s ease both; }
.ag-semana { display: grid; grid-template-columns: repeat(7, 1fr); gap: 0.4rem; height: 180px; }
.ag-semana__col { display: flex; flex-direction: column; align-items: center; gap: 0.25rem; min-width: 0; }
.ag-semana__valor { font-size: 0.68rem; color: var(--tinta-suave); white-space: nowrap; height: 1rem; }
.ag-semana__barra { flex: 1; width: 100%; max-width: 34px; display: flex; align-items: flex-end; background: rgba(22, 53, 40, 0.05); border-radius: 6px; overflow: hidden; }
.ag-semana__barra span { width: 100%; border-radius: 4px 4px 0 0; transition: height 0.6s cubic-bezier(.2,.8,.2,1); }
.ag-semana__dia { font-size: 0.72rem; font-weight: 600; text-transform: capitalize; color: var(--tinta-suave); }
</style>
